import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";

import { createAuthClient } from "better-auth/react";
import { inferAdditionalFields } from "better-auth/client/plugins";
import { API_ENDPOINTS } from "@/constants/api";
import { getConfig } from "@/config";
import {
  getAccountApprovalStatus,
  type ApprovalStatus,
} from "@/services/account-status.service";

const baseURL = getConfig().VITE_DASHBOARD_API_URL;

export const authClient = createAuthClient({
  baseURL,
  // clientId is server-managed (input: false on the API) — declared here only
  // so session.user.clientId stays typed for reads.
  plugins: [
    inferAdditionalFields({
      user: {
        clientId: { type: "string", required: false },
        approvalStatus: { type: "string", required: false },
      },
    }),
  ],
  redirectURL: window.location.origin,
});

// Define the auth context type
type AuthContextType = {
  user: any | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  approvalStatus: ApprovalStatus | null;
  approvalStatusError: string | null;
  refreshApprovalStatus: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    name: string,
    options?: { image?: string; callbackURL?: string }
  ) => Promise<any>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<any>;
  resendVerification: (email: string) => Promise<any>;
  authClient: typeof authClient;
};

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
type AuthProviderProps = {
  children: ReactNode;
};

// Auth provider component
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [approvalStatus, setApprovalStatus] =
    useState<ApprovalStatus | null>(null);
  const [approvalStatusError, setApprovalStatusError] = useState<string | null>(
    null
  );

  const resolveApprovalStatus = useCallback(async () => {
    setApprovalStatus(null);
    setApprovalStatusError(null);

    try {
      setApprovalStatus(await getAccountApprovalStatus());
    } catch (error) {
      console.error("Account approval status check failed:", error);
      setApprovalStatusError(
        "We couldn't verify your account status. Please try again."
      );
    }
  }, []);

  const refreshApprovalStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      await resolveApprovalStatus();
    } finally {
      setIsLoading(false);
    }
  }, [resolveApprovalStatus]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const session = await authClient.getSession();
        if (session && session.data?.user && session.data.user.emailVerified) {
          setUser(session.data.user);
          await resolveApprovalStatus();
        } else {
          setUser(null);
          setApprovalStatus(null);
          setApprovalStatusError(null);
        }
      } catch (error) {
        console.error("Authentication check failed:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [resolveApprovalStatus]);

  // Sign in function
  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    setApprovalStatus(null);
    setApprovalStatusError(null);
    try {
      const response = await authClient.signIn.email({
        email,
        password,
      });
      if (response.error?.status === 401) {
        throw new Error("Invalid email or password");
      }

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response && response.data?.user) {
        setUser(response.data.user);
        await resolveApprovalStatus();
      }
    } catch (error) {
      console.error("Sign in failed:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    setIsLoading(true);
    try {
      await authClient.signOut({
        fetchOptions: {
          onSuccess: () => {
            // Clear user state
            setUser(null);
            setApprovalStatus(null);
            setApprovalStatusError(null);
            // Redirect to sign-in page
            window.location.href = "/signin";
          },
        },
      });
    } catch (error) {
      console.error("Sign out failed:", error);
      setIsLoading(false);
      throw error;
    }
  };

  // Sign up function. The tenant (clientId) and organization are provisioned
  // server-side; invited users join the inviter's organization instead.
  const signUp = async (
    email: string,
    password: string,
    name: string,
    options?: { image?: string; callbackURL?: string }
  ) => {
    setIsLoading(true);
    try {
      const response = await authClient.signUp.email({
        email,
        password,
        name,
        image: options?.image,
        callbackURL:
          options?.callbackURL ??
          window.location.origin + "/signin?emailConfirmed=true",
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      // Note: By default, users are automatically signed in after signup
      // If autoSignIn is false in your auth config, they will need to sign in manually
      if (response && response.data?.user) {
        setUser(response.data.user);
        if (response.data.user.emailVerified) {
          await resolveApprovalStatus();
        }
      }

      return response;
    } catch (error) {
      console.error("Sign up failed:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Password reset request function
  const requestPasswordReset = async (email: string) => {
    setIsLoading(true);
    try {
      const response = await authClient.requestPasswordReset({
        email,
        redirectTo: window.location.origin + "/reset-password",
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response;
    } catch (error) {
      console.error("Password reset request failed:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Sign in with Google function
  const signInWithGoogle = async () => {
    setIsLoading(true);
    try {
      const response = await authClient.signIn.social({
        provider: "google",
        callbackURL: window.location.origin,
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      // After social sign-in, we need to check the session to get the user data
      const session = await authClient.getSession();
      if (session && session.data?.user) {
        setUser(session.data.user);
        await resolveApprovalStatus();
      }
    } catch (error) {
      console.error("Google sign in failed:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Resend verification email function
  const resendVerification = async (email: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          callbackURL: window.location.origin + "/signin?emailConfirmed=true",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to resend verification email"
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Resend verification failed:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Context value
  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    approvalStatus,
    approvalStatusError,
    refreshApprovalStatus,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    requestPasswordReset,
    resendVerification,
    authClient,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
